// Numéro de colis (airwaybill) à partir du contenu d'un code-barres ou d'une
// saisie. Même règle que le backend (routers/colis_routes.py). Seuls les
// airwaybills FedEx et TNT sont acceptés :
//  - FedEx : code 128 de 34 chiffres -> 12 derniers
//    (1288803550340957399600878089574970 -> 878089574970), ou étiquette 2D
//    PDF417 (format MH10.8.3 : n° + destinataire, adresse, CP) ; saisie : 12 chiffres ;
//  - TNT national : code ITF de 16 chiffres, gardé tel quel (1141002544774683) ;
//  - TNT international : code 128 de 28 chiffres -> chiffres 5 à 15 = n° d'expédition
//    (9 chiffres) + n° de pièce (2) : 1100589018220012486031011000 -> 58901822001 ;
//    saisie : 11 chiffres.
// Tout autre code (référence, routage, QR…) est refusé.
export const TYPES_AWB = {
  fedex: { transporteur: "FedEx", label: "FedEx", chiffres: 12 },
  tnt_national: { transporteur: "TNT", label: "TNT national", chiffres: 16 },
  tnt_international: { transporteur: "TNT", label: "TNT international", chiffres: 11 },
};
export const AIDE_AWB = "FedEx (12 chiffres), TNT national (16 chiffres) ou TNT international (11 chiffres)";

const RS = "\x1e";
const GS = "\x1d";
const PAYS = { 250: "FR", 276: "DE", "056": "BE", 380: "IT", 724: "ES", 442: "LU", 756: "CH", 528: "NL", 826: "GB", 620: "PT", 840: "US" };
const SCAC = { FDE: "FedEx", FDX: "FedEx", FDEG: "FedEx", FXG: "FedEx", UPSN: "UPS" };

function lireMH10(brut) {
  const i = brut.indexOf("[)>" + RS);
  if (i < 0) return null;
  const records = brut.slice(i + 4).split(RS);
  const r01 = records.find((r) => r.startsWith("01" + GS));
  if (!r01) return null;
  const f = r01.split(GS);
  const net = (x) => (x || "").trim();
  let numero = net(f[4]).replace(/\s/g, "").toUpperCase();
  const transporteur = SCAC[net(f[5]).toUpperCase()] || (net(f[5]) || null);
  // Champ 31Z (FedEx) : les 34 chiffres du code 128 -> 12 derniers.
  const r06 = records.find((r) => r.startsWith("06" + GS));
  const z31 = r06?.split(GS).find((x) => x.startsWith("31Z"));
  if (z31 && /^\d{34}$/.test(z31.slice(3))) numero = z31.slice(-12);
  else if (transporteur === "FedEx" && /^\d{16}$/.test(numero)) numero = numero.slice(0, 12); // n° + code formulaire
  if (!numero) return null;
  const cp = net(f[1]).startsWith("02") ? net(f[1]).slice(2) : net(f[1]);
  const etiquette = {
    destinataire: net(f[15]), adresse: net(f[12]), cp, ville: net(f[13]), departement: net(f[14]),
    pays: PAYS[net(f[2])] || net(f[2]), poids: net(f[10]), colis: net(f[9]),
  };
  Object.keys(etiquette).forEach((k) => { if (!etiquette[k]) delete etiquette[k]; });
  return { numero, transporteur, etiquette };
}

const awb = (type, numero, extra = {}) => ({ numero, type, transporteur: TYPES_AWB[type].transporteur, ...extra });
const tntInter = (n) => awb("tnt_international", n, { etiquette: { expedition: n.slice(0, 9), piece: n.slice(9) } });

export function lireCode(brut) {
  const mh = lireMH10(brut || "");
  if (mh) {
    return mh.transporteur === "FedEx" && /^\d{12}$/.test(mh.numero)
      ? awb("fedex", mh.numero, { etiquette: mh.etiquette })
      : { numero: mh.numero, type: null, transporteur: null };
  }
  // eslint-disable-next-line no-control-regex
  const s = (brut || "").replace(/[\s\x00-\x1f]/g, "").toUpperCase();
  if (/^\d{34}$/.test(s)) return awb("fedex", s.slice(-12));
  if (/^\d{28}$/.test(s)) return tntInter(s.slice(4, 15));
  if (/^\d{16}$/.test(s)) return awb("tnt_national", s);
  if (/^\d{12}$/.test(s)) return awb("fedex", s);
  if (/^\d{11}$/.test(s)) return tntInter(s);
  return { numero: s, type: null, transporteur: null };
}

export const numeroValide = (n) => !!lireCode(n).type;

export function lienSuivi(c) {
  if (!c?.numero) return null;
  if (c.transporteur === "FedEx") return `https://www.fedex.com/fedextrack/?trknbr=${c.numero}`;
  return null;
}

export const libelleType = (c) => TYPES_AWB[c?.type_awb || c?.type]?.label || c?.transporteur || null;

// Affichage comme sur les étiquettes : 878089574970 -> 8780 8957 4970 ;
// TNT international (11 chiffres) : 58901822001 -> 58901 8220 01.
export const numeroLisible = (n) => (/^\d{11}$/.test(n || "")
  ? `${n.slice(0, 5)} ${n.slice(5, 9)} ${n.slice(9)}`
  : (n || "").replace(/(.{4})(?=.)/g, "$1 "));
